import React from 'react';
import classNames from 'classnames';
import Immutable from 'immutable';
import { useSelector } from 'react-redux';
import { Block, Paragraph } from '@uncinc/react-drupal-core';
import { Container, WysiwygField, useWindowDimensions } from '@uncinc/uncinc-react-kitchen-sink';
import { ImmutableEntity } from '@uncinc/react-drupal-core/types';

import {
  BlockEventInfo,
  Breadcrumbs,
  DynamicMediaItem,
  RelatedContentByTheme,
  Tag,
} from '../../../../components';
import { APP_OPTIONS, DEFAULT_LANGUAGE, SCREEN_WIDE } from '../../../../config/constants';
import { Breadcrumb, BreadcrumbState } from '../../../../types/uncinc_profile/helpers/breadcrumbs';
import { NodeEvent } from '../../../../types/uncinc_profile/nodes';
import { createSearchFacetUrl } from '../../../../helpers/createSearchUrl';

import './style.scss';

interface EventPageProps extends React.ComponentPropsWithoutRef<'div'> {
  entity: ImmutableEntity<NodeEvent>;
}

const EventPage: React.FC<EventPageProps> = ({ entity }) => {
  const { width } = useWindowDimensions();

  const breadcrumbTopical = useSelector<BreadcrumbState, Breadcrumb | undefined>(
    (state) => {
      const breadcrumbs = state.getIn(['entity', 'breadcrumbs'], Immutable.List).toJS();

      if (breadcrumbs.length === 0) {
        return undefined;
      }

      // We want to get the crumb before the current page.
      return breadcrumbs[breadcrumbs.length - 2];
    },
  );

  const classes = classNames('NodeEventPage');
  const event = entity.toJS();
  const title = event.title[0].value;
  const media = event.field_media_item[0];
  const body = event.body[0]?.processed;

  const theme = event.field_themes[0]?.entity?.name[0].value;
  const audience = event.field_target_audiences[0]?.entity?.name[0].value;

  const contactFormBlockEntity = entity.getIn(['field_contact_form_block', 0, 'entity'], Immutable.Map());

  return (
    <article itemType="http://schema.org/WebPage" className={classes}>
      <Container>
        <div className="row">
          <div className="col-12">
            <Breadcrumbs />
          </div>
        </div>

        <div className="row">
          <div className="NodeEventPage__hero col-12 col-lg-7">
            {(theme || audience) && (
              <div className="tags NodeEventPage__terms">
                {theme && (
                  <Tag
                    type="theme"
                    name={theme}
                    stretchClickableArea
                    target="_self"
                    link={breadcrumbTopical && breadcrumbTopical.url ? createSearchFacetUrl({
                      pathname: breadcrumbTopical.url,
                      index: APP_OPTIONS.parameters.searchkitIndexTopical,
                      facetType: 'refinementList',
                      attribute: 'theme',
                      value: theme,
                    }) : ''}
                  />
                )}

                {audience && (
                  <Tag
                    type="article-type"
                    name={audience}
                    stretchClickableArea
                    target="_self"
                    link={breadcrumbTopical && breadcrumbTopical.url ? createSearchFacetUrl({
                      pathname: breadcrumbTopical.url,
                      index: APP_OPTIONS.parameters.searchkitIndexTopical,
                      facetType: 'refinementList',
                      attribute: 'targetAudience',
                      value: audience,
                    }) : ''}
                  />
                )}
              </div>
            )}
            <div className="NodeEventPage__hero__text">
              <h1 itemProp="name">{title}</h1>
              {body ? (
                <WysiwygField
                  property="schema:text"
                  html={body}
                  className="body--large"
                />
              ) : null}
            </div>

            {width < SCREEN_WIDE && (
              <div className="content-section">
                <BlockEventInfo event={event} />
              </div>
            )}

            {media && (
              <div className="content-section">
                <div className="NodeEventPage__image">
                  <DynamicMediaItem media={media} imageAspectRatio="2_1" aboveTheFold />
                </div>
              </div>
            )}

            <div className="content-section">
              {event.field_paragraphs ? (
                <div className="NodeEventPage__paragraphs">
                  {event.field_paragraphs.map(({ target_id: id, entity: paragraph }) => {
                    const language = paragraph.langcode[0].value || DEFAULT_LANGUAGE;
                    return (
                      <Paragraph
                        key={`paragraph:${id}:${language}`}
                        id={id}
                        entity={Immutable.fromJS(paragraph)}
                      />
                    );
                  })}
                </div>
              ) : null}
            </div>
          </div>

          {width >= SCREEN_WIDE && (
            <div className="NodeEventPage__sidebar col-lg-5 col-xl-4 offset-xl-1 d-none d-lg-block mt-6">
              <div className="sidebar-section">
                <BlockEventInfo event={event} />
              </div>
            </div>
          )}
        </div>

        {contactFormBlockEntity && (
          <Block entity={contactFormBlockEntity} />
        )}
      </Container>

      <RelatedContentByTheme
        themeIds={event.field_themes.map((term) => term.target_id)}
        excludeNid={event.nid[0].value}
      />
    </article>
  );
};

export default EventPage;
